import { forwardRef, type InputHTMLAttributes, type ReactNode, useId } from 'react';
import { cn } from './utils';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: ReactNode;
  description?: ReactNode;
  error?: boolean;
}

const inputStyles =
  'w-4 h-4 mt-0.5 accent-accent cursor-pointer rounded border-neutral-300 focus:ring-2 focus:ring-accent disabled:cursor-not-allowed disabled:opacity-50';

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  function Checkbox(props, ref) {
    const {
      label,
      description,
      error = false,
      className = '',
      id: providedId,
      disabled,
      ...rest
    } = props;

    const generatedId = useId();
    const id = providedId || generatedId;
    const descriptionId = description ? `${id}-description` : undefined;

    const inputClassName = cn(
      inputStyles,
      error && 'ring-2 ring-error-border',
      className,
    );

    if (!label && !description) {
      return (
        <input
          ref={ref}
          type="checkbox"
          id={id}
          className={inputClassName}
          disabled={disabled}
          aria-invalid={error || undefined}
          {...rest}
        />
      );
    }

    return (
      <div className="flex items-start gap-2">
        <input
          ref={ref}
          type="checkbox"
          id={id}
          className={inputClassName}
          disabled={disabled}
          aria-describedby={descriptionId}
          aria-invalid={error || undefined}
          {...rest}
        />
        <div className="flex-1">
          {label && (
            <label
              htmlFor={id}
              className={cn(
                'text-sm select-none',
                disabled ? 'text-neutral-400 cursor-not-allowed' : 'text-neutral-800 cursor-pointer',
              )}
            >
              {label}
            </label>
          )}
          {description && (
            <p id={descriptionId} className="text-xs text-neutral-500 mt-0.5">
              {description}
            </p>
          )}
        </div>
      </div>
    );
  },
);
